import { useMobileSubscription } from '@/hooks/use-mobile-subscription';
import { useAuth } from '@/contexts/auth-context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Check, RotateCcw } from 'lucide-react';
import { Capacitor } from '@capacitor/core';

export function SubscriptionTest() {
  const { user } = useAuth();
  const {
    products,
    isLoading,
    isInitialized,
    currentSubscription,
    purchaseProduct,
    restorePurchases,
    isSubscribed
  } = useMobileSubscription(user?.uid);

  const handlePurchase = async (productId: string) => {
    if (!user) {
      alert('Please log in first');
      return;
    }

    try {
      const transaction = await purchaseProduct(productId, user.uid);
      if (transaction) {
        alert('Purchase successful!');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Purchase failed');
    }
  };

  const handleRestore = async () => {
    if (!user) {
      alert('Please log in first');
      return;
    }

    try {
      const count = await restorePurchases(user.uid);
      alert(`Restored ${count} purchases`);
    } catch (error) {
      console.error('Restore error:', error);
      alert('Restore failed');
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Subscription Test</CardTitle>
          <CardDescription>
            Test the subscription functionality on this platform
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <strong>Platform:</strong> {Capacitor.getPlatform()}
            </div>
            <div>
              <strong>Native Platform:</strong> {Capacitor.isNativePlatform() ? 'Yes' : 'No'}
            </div>
            <div>
              <strong>Initialized:</strong> {isInitialized ? 'Yes' : 'No'}
            </div>
            <div>
              <strong>Loading:</strong> {isLoading ? 'Yes' : 'No'}
            </div>
            <div>
              <strong>Subscribed:</strong> {isSubscribed ? 'Yes' : 'No'}
            </div>
            <div>
              <strong>Products Count:</strong> {products.length}
            </div>
            <div>
              <strong>Expected:</strong> 1 (Monthly only)
            </div>
          </div>

          {currentSubscription && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="font-semibold text-green-800">Current Subscription</h4>
              <p className="text-sm text-green-700">
                Status: {currentSubscription.status}
              </p>
              <p className="text-sm text-green-700">
                Product: {currentSubscription.productId}
              </p>
              <p className="text-sm text-green-700">
                Expires: {currentSubscription.expirationDate?.toLocaleDateString() || 'Never'}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <h4 className="font-semibold">Available Products</h4>
            {products.length > 0 ? (
              <div className="space-y-2">
                {products.map((product) => (
                  <div key={product.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <div className="font-medium">{product.title}</div>
                      <div className="text-sm text-gray-600">{product.description}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">${product.price}</span>
                      <Button
                        size="sm"
                        onClick={() => handlePurchase(product.productId)}
                        disabled={isLoading || !isInitialized}
                      >
                        {isLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No products available</p>
            )}
          </div>

          {Capacitor.isNativePlatform() && (
            <div className="flex gap-2">
              <Button
                onClick={handleRestore}
                disabled={isLoading || !isInitialized}
                variant="outline"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Restore Purchases
              </Button>
            </div>
          )}

          {!Capacitor.isNativePlatform() && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                Subscription functionality is only available on mobile devices. 
                Please test on iOS or Android.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
